import * as React from "react";
import * as ReactDOM from "react-dom";
import clsx from "clsx";
import { Building2 } from "lucide-react";
import { normalizeAvatarUrl } from "../ImageUpload/logic/avatarImageUtils";
import * as s from "./OrgThumbnail.style.css";

export interface OrgThumbnailProps {
  name: string;
  logoUrl?: string | null;
  /** Rendered width and height in px. */
  size?: number;
  onClick?: () => void;
}

export function OrgThumbnail({ name, logoUrl, size = 20, onClick }: OrgThumbnailProps) {
  const ref = React.useRef<HTMLElement>(null);
  const [tooltipPos, setTooltipPos] = React.useState<{ bottom: number; left: number } | null>(null);
  const [imageFailed, setImageFailed] = React.useState(false);
  const src = !imageFailed ? normalizeAvatarUrl(logoUrl) || null : null;

  React.useEffect(() => setImageFailed(false), [logoUrl]);

  const showTooltip = () => {
    const rect = ref.current?.getBoundingClientRect();
    if (rect) {
      setTooltipPos({ bottom: window.innerHeight - rect.top + 8, left: rect.left + rect.width / 2 });
    }
  };
  const hideTooltip = () => setTooltipPos(null);

  const content = src ? (
    <img src={src} alt="" className={s.img} onError={() => setImageFailed(true)} />
  ) : (
    <Building2 size={Math.round(size * 0.6)} strokeWidth={2} aria-hidden />
  );

  const common = {
    className: clsx(s.thumb, onClick && s.clickable),
    style: { width: size, height: size },
    onMouseEnter: showTooltip,
    onMouseLeave: hideTooltip,
    onFocus: showTooltip,
    onBlur: hideTooltip,
  };

  const tooltip = tooltipPos ? (
    <div
      className={s.tooltipPortal}
      style={{ position: "fixed", bottom: tooltipPos.bottom, left: tooltipPos.left, transform: "translateX(-50%)" }}
      role="tooltip"
    >
      <div className={s.tooltipInner}>
        {name}
        <div className={s.tooltipCaret} />
      </div>
    </div>
  ) : null;

  return (
    <>
      {onClick ? (
        <button
          {...common}
          ref={ref as React.RefObject<HTMLButtonElement>}
          type="button"
          aria-label={name}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            hideTooltip();
            onClick();
          }}
        >
          {content}
        </button>
      ) : (
        <span {...common} ref={ref as React.RefObject<HTMLSpanElement>} role="img" aria-label={name}>
          {content}
        </span>
      )}
      {typeof document !== "undefined" && ReactDOM.createPortal(tooltip, document.body)}
    </>
  );
}
